import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import api, { formatApiError } from "../api";
import { BrutalButton, BrutalCard, BrutalInput } from "./Brutal";
import { copyToClipboard } from "../lib/clipboard";
import { Copy, TreeStructure, Plus } from "@phosphor-icons/react";
import { toast } from "sonner";

/** v414.48 — owner sets up a sub-distributor ON BEHALF of a distributor so
 * the parent can't misconfigure pricing. The sub is created UNDER the parent
 * with the markup applied from second zero; temp password shown once. */
export function SubDistributorCard({ distributor }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [marginType, setMarginType] = useState("percent");
  const [marginValue, setMarginValue] = useState("");
  const [wellnessType, setWellnessType] = useState("percent");
  const [wellnessValue, setWellnessValue] = useState("");
  const [busy, setBusy] = useState(false);
  const [creds, setCreds] = useState(null);

  const create = async (e) => {
    e.preventDefault();
    const password = `Axis-${Math.random().toString(36).slice(2, 10)}`;
    setBusy(true);
    try {
      const { data } = await api.post("/distributors", {
        name: name.trim(),
        email: email.trim(),
        password,
        parent_id: distributor.id,
        margin_surcharge: parseFloat(marginValue),
        margin_type: marginType,
        wellness_margin_surcharge: wellnessValue.trim() === "" ? null : parseFloat(wellnessValue),
        wellness_margin_type: wellnessValue.trim() === "" ? null : wellnessType,
      });
      setCreds({
        name: data.name, email: data.email, password,
        code: data.distributor_code,
        email_sent: data.onboarding_email?.sent,
      });
      setOpen(false); setName(""); setEmail(""); setMarginValue(""); setWellnessValue("");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <BrutalCard data-testid="sub-distributor-card">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h3 className="font-semibold flex items-center gap-2">
            <TreeStructure size={18} weight="bold" className="text-[#8a6a1c]" />
            {t("sub_dist.title")}
          </h3>
          <p className="text-xs text-zinc-500 mt-0.5">{t("sub_dist.hint", { name: distributor.name })}</p>
        </div>
        {!open && (
          <BrutalButton size="sm" variant="secondary" onClick={() => setOpen(true)} data-testid="sub-dist-add-btn">
            <Plus size={14} weight="bold" /> {t("sub_dist.add_btn")}
          </BrutalButton>
        )}
      </div>

      {open && (
        <form onSubmit={create} className="mt-3 flex flex-wrap gap-2 items-end">
          <div className="flex-1 min-w-[140px]">
            <label className="block text-xs font-semibold mb-1">{t("common.name")}</label>
            <BrutalInput value={name} onChange={(e) => setName(e.target.value)} required minLength={2} data-testid="sub-dist-name-input" />
          </div>
          <div className="flex-1 min-w-[180px]">
            <label className="block text-xs font-semibold mb-1">{t("common.email")}</label>
            <BrutalInput type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="sub-dist-email-input" />
          </div>
          <div className="w-32">
            <label className="block text-xs font-semibold mb-1">{t("sub_dist.margin_type_label")}</label>
            <select value={marginType} onChange={(e) => setMarginType(e.target.value)}
              className="w-full h-10 px-2 text-sm border-2 border-zinc-900 rounded-lg bg-white"
              data-testid="sub-dist-margin-type">
              <option value="percent">{t("sub_dist.margin_percent")}</option>
              <option value="flat">{t("sub_dist.margin_flat")}</option>
            </select>
          </div>
          <div className="w-28">
            <label className="block text-xs font-semibold mb-1">
              {marginType === "percent" ? t("sub_dist.margin_value_pct") : t("sub_dist.margin_value_flat")}
            </label>
            <BrutalInput type="number" step="0.01" min="0" value={marginValue} required
              onChange={(e) => setMarginValue(e.target.value)} data-testid="sub-dist-margin-input" />
          </div>
          <div className="w-32">
            <label className="block text-xs font-semibold mb-1">{t("sub_dist.wellness_type_label")}</label>
            <select value={wellnessType} onChange={(e) => setWellnessType(e.target.value)}
              className="w-full h-10 px-2 text-sm border-2 border-zinc-900 rounded-lg bg-white"
              data-testid="sub-dist-wellness-type">
              <option value="percent">{t("sub_dist.margin_percent")}</option>
              <option value="flat">{t("sub_dist.margin_flat")}</option>
            </select>
          </div>
          <div className="w-28">
            <label className="block text-xs font-semibold mb-1">{t("sub_dist.wellness_value_label")}</label>
            <BrutalInput type="number" step="0.01" min="0" value={wellnessValue}
              onChange={(e) => setWellnessValue(e.target.value)} data-testid="sub-dist-wellness-input" />
          </div>
          <BrutalButton type="submit" size="sm" disabled={busy} data-testid="sub-dist-create-btn">
            {busy ? t("common.loading") : t("sub_dist.create_btn")}
          </BrutalButton>
          <BrutalButton type="button" size="sm" variant="secondary" onClick={() => setOpen(false)} data-testid="sub-dist-cancel-btn">
            {t("common.cancel")}
          </BrutalButton>
          <p className="w-full text-[11px] text-zinc-500">{t("sub_dist.margin_note", { name: distributor.name })} {t("sub_dist.wellness_note")}</p>
        </form>
      )}

      {creds && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="sub-dist-creds-dialog">
          <div className="bg-white rounded-2xl border border-zinc-200 shadow-xl w-full max-w-sm p-5">
            <h2 className="text-lg font-semibold mb-1">{t("sub_dist.creds_title")}</h2>
            <p className="text-sm text-zinc-600 mb-3">
              {t("sub_dist.creds_subtitle", { name: creds.name, code: creds.code, parent: distributor.name })}
              {!creds.email_sent && <span className="text-amber-700"> {t("co_logins.email_failed_note")}</span>}
            </p>
            <div className="bg-zinc-50 border border-zinc-200 rounded-lg p-3 font-mono text-sm space-y-1" data-testid="sub-dist-creds-value">
              <div><strong>{t("common.email")}:</strong> {creds.email}</div>
              <div><strong>{t("common.password")}:</strong> {creds.password}</div>
            </div>
            <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mt-3">
              {t("distributors.temp_pw_once_note")}
            </p>
            <BrutalButton className="w-full mt-3"
              onClick={() => copyToClipboard(
                `Email: ${creds.email}\nPassword: ${creds.password}\nLogin: ${window.location.origin}/login`,
                t("common.copied"))}
              data-testid="sub-dist-creds-copy">
              <Copy size={14} weight="bold" /> {t("distributors.copy_creds")}
            </BrutalButton>
            <BrutalButton variant="secondary" className="w-full mt-2" onClick={() => setCreds(null)} data-testid="sub-dist-creds-done">
              {t("common.done")}
            </BrutalButton>
          </div>
        </div>
      )}
    </BrutalCard>
  );
}
